// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0

import React from 'react';
import './StreamComponent.css';
import * as gameliftstreamssdk from './gamelift-streams-websdk/gameliftstreams-1.1.0';
import { GaijinAuthService, GaijinUser } from './auth/GaijinAuthService';
import StatsOverlay from './StatsOverlay';

interface StreamComponentProps {
    signOut: () => void;
    user: GaijinUser | null;
    apiEndpoint: string;
}

const SUPPORTED_REGIONS = ['ap-northeast-1', 'eu-central-1', 'eu-west-1', 'us-east-1', 'us-east-2', 'us-west-2'];
const DEFAULT_REGION = 'eu-central-1';
const REGION_LABELS: Record<string, string> = {
    'ap-northeast-1': 'ap-northeast-1 · Tokyo',
    'eu-central-1': 'eu-central-1 · Frankfurt',
    'eu-west-1': 'eu-west-1 · Ireland',
    'us-east-1': 'us-east-1 · N. Virginia',
    'us-east-2': 'us-east-2 · Ohio',
    'us-west-2': 'us-west-2 · Oregon'
};

const YOUTRACK_URL = (process.env.REACT_APP_YOUTRACK_URL || 'https://youtrack.gaijin.team').replace(/\/$/, '');
const YOUTRACK_PROJECT = process.env.REACT_APP_YOUTRACK_PROJECT || '';

export function youtrackDraftUrl(baseUrl: string, project: string, description: string): string {
    const params = new URLSearchParams({ description });
    if (!project) {
        return `${baseUrl}/newIssue?${params.toString()}`;
    }
    params.set('project', project);
    return `${baseUrl}/projects/${encodeURIComponent(project)}/newIssue?${params.toString()}`;
}

type KeyStroke = { code: string; shift: boolean };
type SessionStatusAnswer = { status: string; signalResponse?: string };

function buildUsKeyMap(): Record<string, KeyStroke> {
    const map: Record<string, KeyStroke> = {};
    for (let offset = 0; offset < 26; offset++) {
        const lower = String.fromCharCode(97 + offset);
        const upper = String.fromCharCode(65 + offset);
        const code = `Key${upper}`;
        map[lower] = { code, shift: false };
        map[upper] = { code, shift: true };
    }
    const digitRow: Array<[string, string]> = [['1', '!'], ['2', '@'], ['3', '#'], ['4', '$'], ['5', '%'], ['6', '^'], ['7', '&'], ['8', '*'], ['9', '('], ['0', ')']];
    for (const [digit, shifted] of digitRow) {
        const code = `Digit${digit}`;
        map[digit] = { code, shift: false };
        map[shifted] = { code, shift: true };
    }
    const symbolRow: Array<[string, string, string]> = [
        ['-', '_', 'Minus'], ['=', '+', 'Equal'], ['[', '{', 'BracketLeft'], [']', '}', 'BracketRight'], ['\\', '|', 'Backslash'],
        [';', ':', 'Semicolon'], ["'", '"', 'Quote'], [',', '<', 'Comma'], ['.', '>', 'Period'], ['/', '?', 'Slash'], ['`', '~', 'Backquote']
    ];
    for (const [base, shifted, code] of symbolRow) {
        map[base] = { code, shift: false };
        map[shifted] = { code, shift: true };
    }
    map[' '] = { code: 'Space', shift: false };
    return map;
}

const US_KEY_MAP = buildUsKeyMap();
const PASTE_KEYSTROKE_DELAY_MS = 12;
const MAX_PASTE_CHARS = 1024;
const CLIPBOARD_READ_TIMEOUT_MS = 3000;
// Outlasts the proxy's own bound on a start (placement-timeout 10 min + poll + 30 s), so the proxy ends a slow start, not this timer.
const SESSION_WAIT_TIMEOUT_MS = 660000;
const MODIFIER_KEYS: Array<[string, string]> = [
    ['Control', 'ControlLeft'], ['Control', 'ControlRight'], ['Meta', 'MetaLeft'], ['Meta', 'MetaRight'],
    ['Alt', 'AltLeft'], ['Alt', 'AltRight'], ['Shift', 'ShiftLeft'], ['Shift', 'ShiftRight']
];

function isLocalControl(target: EventTarget | null): boolean {
    const element = target as HTMLElement | null;
    if (!element || !element.tagName) {
        return false;
    }
    if (element.isContentEditable) {
        return true;
    }
    const tagName = element.tagName;
    return tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT'
        || tagName === 'BUTTON' || tagName === 'A' || tagName === 'OPTION' || tagName === 'LABEL';
}

export enum StreamState {
    STOPPED = 1,
    LOADING,
    RUNNING,
    ERROR
}

interface StreamComponentState {
    status: StreamState;
    sgId: string;
    appId: string;
    buildLabel: string;
    sessionId: string;
    lastSessionId: string;
    regions: string[];
    inputEnabled: boolean;
    isStreamStarting: boolean;
    isStatsVisible: boolean;
    isProfilerArmed: boolean;
    isRejoinOpen: boolean;
    elapsed: number;
    perfStats: any;
}

class StreamComponent extends React.Component<StreamComponentProps, StreamComponentState> {
    gameliftstreams?: gameliftstreamssdk.GameLiftStreams;

    constructor(props: StreamComponentProps) {
        super(props);

        const urlParams = new URLSearchParams(window.location.search);
        const regionParam = urlParams.get('region') ?? '';

        this.state = {
            status: StreamState.STOPPED,
            sgId: urlParams.get('sg') ?? '',
            appId: urlParams.get('app') ?? '',
            buildLabel: urlParams.get('build') ?? '',
            sessionId: '',
            lastSessionId: '',
            regions: [SUPPORTED_REGIONS.includes(regionParam) ? regionParam : DEFAULT_REGION],
            inputEnabled: false,
            isStreamStarting: false,
            isStatsVisible: false,
            isProfilerArmed: false,
            isRejoinOpen: false,
            elapsed: 0,
            perfStats: {}
        };

        this.performanceStatsCallback = this.performanceStatsCallback.bind(this);
        this.createStreamSession = this.createStreamSession.bind(this);
        this.createStreamSessionConnection = this.createStreamSessionConnection.bind(this);
        this.closeConnection = this.closeConnection.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.enableFullScreen = this.enableFullScreen.bind(this);
    }

    componentDidMount(): void {
        this.resetGameLiftStreamsSDK();
    }

    componentWillUnmount(): void {
        // Sign Out unmounts without closeConnection(); stop in-flight paste + close the session.
        this.unmounted = true;
        this.stopElapsedTimer();
        this.gameliftstreams?.close();
    }

    private resetGameLiftStreamsSDK() {
        this.gameliftstreams = new gameliftstreamssdk.GameLiftStreams({
            videoElement: this.getVideoElement(),
            audioElement: this.getAudioElement(),
            inputConfiguration: {
                setCursor: 'visibility',
                autoPointerLock: 'fullscreen',
                keyboardFilter: this.keyboardFilter
            },
            clientConnection: {
                performanceStats: this.performanceStatsCallback
            }
        });
    }

    private getVideoElement(): HTMLVideoElement {
        return document.getElementById(`StreamVideoElement`) as HTMLVideoElement;
    }

    private getAudioElement(): HTMLAudioElement {
        return document.getElementById(`StreamAudioElement`) as HTMLAudioElement;
    }

    private performanceStatsCallback(perfStats: any) {
        this.setState({ perfStats });
    }

    private toggleStats = () => {
        this.setState((prevState) => ({ isStatsVisible: !prevState.isStatsVisible }));
    };

    // ALT+F11 arms the in-game Dagor profiler and F11 writes .game_logs/ring-*.dap. The browser
    // consumes both chords before the stream sees them, so they are handed to the SDK directly.
    private sendKeys = (events: KeyboardEvent[], releaseOnFailure: KeyboardEvent[] = []): boolean => {
        const session = this.gameliftstreams;
        if (!session || this.state.status !== StreamState.RUNNING) {
            return false;
        }
        let transmitted = true;
        for (const event of events) {
            transmitted = session.processKeyboardEvent(event) && transmitted;
        }
        if (!transmitted) {
            // A keydown may have landed without its keyup; best-effort release so the key
            // isn't left stuck down on the remote.
            for (const event of releaseOnFailure) {
                session.processKeyboardEvent(event);
            }
        }
        return transmitted;
    };

    private toggleProfiler = () => {
        const alt = { key: 'Alt', code: 'AltLeft', location: KeyboardEvent.DOM_KEY_LOCATION_LEFT };
        if (!this.sendKeys([
            new KeyboardEvent('keydown', { ...alt, altKey: true }),
            new KeyboardEvent('keydown', { key: 'F11', code: 'F11', altKey: true }),
            new KeyboardEvent('keyup', { key: 'F11', code: 'F11', altKey: true }),
            new KeyboardEvent('keyup', { ...alt, altKey: false })
        ], [
            new KeyboardEvent('keyup', { key: 'F11', code: 'F11', altKey: false }),
            new KeyboardEvent('keyup', { ...alt, altKey: false })
        ])) {
            alert('Alt+F11 may not have reached the stream — check the in-game profiler indicator before capturing.');
            return;
        }
        this.setState((prevState) => ({ isProfilerArmed: !prevState.isProfilerArmed }));
    };

    private dumpProfiler = () => {
        if (!this.sendKeys([
            new KeyboardEvent('keydown', { key: 'F11', code: 'F11' }),
            new KeyboardEvent('keyup', { key: 'F11', code: 'F11' })
        ])) {
            alert('F11 was not sent to the stream. No capture was written.');
        }
    };

    private currentSessionArn(): string {
        return this.state.lastSessionId;
    }

    // Must register while the session is active; GameLift then uploads session files to S3 on end.
    private registerSessionFilesExport(sessionArn: string, token: string) {
        // The streaming proxy owns the upstream session and reports no ARN — nothing to register.
        if (!sessionArn) {
            return;
        }
        fetch(`${this.props.apiEndpoint}/export-session-files`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ SessionIdentifier: sessionArn })
        }).then(async (response) => {
            if (!response.ok) {
                const errorBody = await response.text().catch(() => '');
                console.error(`Session files export registration failed: ${response.status} ${errorBody}`);
            }
        }).catch((e) => console.warn('Session files export registration failed:', e));
    }

    private reportBug = () => {
        const descriptionLines = [
            ...(this.state.buildLabel ? [`### ${this.state.buildLabel}`, ''] : []),
            '**Description:**',
            '',
            '',
            '**WTR:**',
            '1. ',
            '',
            '**Result:**',
            '',
            '',
            '**Expected result:**',
            '',
            '',
            '---',
            `Session ARN: \`${this.currentSessionArn() || 'none (streaming proxy)'}\``,
            `Application ID: \`${this.state.appId || 'unknown'}\``,
            `Stream Group ID: \`${this.state.sgId || 'unknown'}\``,
            `Region: ${this.state.regions[0]}`
        ];

        window.open(
            youtrackDraftUrl(YOUTRACK_URL, YOUTRACK_PROJECT, descriptionLines.join('\n')),
            '_blank', 'noopener');
    };

    private handleError(e: any) {
        this.setState({ isStreamStarting: false });
        const message = e.response
            ? `${e.response.status || 'Unknown'} - ${e.message || 'Unknown error'}`
            : (e.message || 'Unknown error');
        alert(`Error: ${message}`);
    }

    private handleTimeout(arn: string) {
        alert(`Error: Stream session creation timed out (${arn})`);
    }

    private async createStreamSession() {
        this.setState({ isStreamStarting: true });
        try {
            const signalRequest = await this.gameliftstreams?.generateSignalRequest();
            const payload = {
                AppIdentifier: this.state.appId,
                SGIdentifier: this.state.sgId,
                SignalRequest: signalRequest ?? '',
                Regions: this.state.regions
            };

            const token = GaijinAuthService.getAccessToken();
            if (!token) {
                throw new Error('No authentication token available');
            }

            const response = await fetch(`${this.props.apiEndpoint}/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`API request failed: ${response.status} ${errorText}`);
            }

            const data = await response.json();
            // An answer already in the start response means the session is ACTIVE (the streaming
            // proxy negotiated it) — there is nothing to poll, and GetStream would answer with
            // GameLift Streams' own SDP instead of the proxy's.
            if (data.signalResponse) {
                const arn = data.arn ?? '';
                this.setState((prevState) => ({
                    ...prevState,
                    lastSessionId: arn
                }));
                this.registerSessionFilesExport(arn, token);
                await this.startStream(data.signalResponse);
                return;
            }
            await this.waitForACTIVE(data.arn, this.state.sgId);
        } catch (e) {
            this.handleError(e);
        }
    }

    private async createStreamSessionConnection() {
        if (!/^arn:[^:]+:gameliftstreams:[^:]*:[^:]*:streamsession\/[^/]+\/[^/]+$/.test(this.state.sessionId)) {
            this.handleError(new Error('Enter the full stream session ARN (arn:aws:gameliftstreams:...:streamsession/<streamGroupId>/<sessionId>) — copy it from the top bar with "Copy ARN" while the session is live'));
            return;
        }
        this.setState({ isStreamStarting: true });
        try {
            const signalRequest = await this.gameliftstreams?.generateSignalRequest();
            const payload = {
                SessionIdentifier: this.state.sessionId,
                SignalRequest: signalRequest ?? '',
            };
            const token = GaijinAuthService.getAccessToken();
            if (!token) {
                throw new Error('No authentication token available');
            }

            const response = await fetch(`${this.props.apiEndpoint}/reconnect`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`API request failed: ${response.status} ${errorText}`);
            }

            const data = await response.json();
            this.setState((prevState) => ({
                ...prevState,
                lastSessionId: this.state.sessionId
            }));
            this.registerSessionFilesExport(this.state.sessionId, token);
            await this.startStream(data.signalResponse);
        } catch (e) {
            this.handleError(e);
        }
    }

    private async pollSessionStatus(arn: string, sg: string, token: string): Promise<SessionStatusAnswer | undefined> {
        let response: Response;
        try {
            response = await fetch(
                `${this.props.apiEndpoint}/session/${encodeURIComponent(sg)}/${encodeURIComponent(arn)}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
        } catch {
            return undefined;
        }

        if (response.status >= 500) {
            return undefined;
        }
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`API request failed: ${response.status} ${errorText}`);
        }
        return response.json();
    }

    async waitForACTIVE(arn: string, sg: string, timeoutMs: number = SESSION_WAIT_TIMEOUT_MS) {
        const startTime = Date.now();
        const token = GaijinAuthService.getAccessToken();

        if (!token) {
            this.handleError(new Error('No authentication token available'));
            this.setState({ isStreamStarting: false });
            return;
        }

        while (Date.now() - startTime < timeoutMs) {
            if (this.unmounted) {
                return;
            }
            try {
                const data = await this.pollSessionStatus(arn, sg, token);
                if (this.unmounted) {
                    return;
                }

                if (data?.status === 'ACTIVE') {
                    this.setState((prevState) => ({
                        ...prevState,
                        lastSessionId: arn
                    }));
                    this.registerSessionFilesExport(arn, token);
                    await this.startStream(data.signalResponse ?? '');
                    return;
                }
                if (data?.status === 'ERROR' || data?.status === 'TERMINATED') {
                    this.handleError(new Error(`Stream session entered terminal state: ${data.status}`));
                    this.setState({ isStreamStarting: false });
                    return;
                }
                await new Promise(resolve => setTimeout(resolve, 1000));
            } catch (e) {
                if (this.unmounted) {
                    return;
                }
                this.handleError(e);
                this.setState({ isStreamStarting: false });
                return;
            }
        }
        if (this.unmounted) {
            return;
        }
        this.handleTimeout(arn);
        this.setState({ isStreamStarting: false });
    }

    private async startStream(signalResponse: string) {
        if (this.unmounted) {
            return;
        }
        await this.gameliftstreams?.processSignalResponse(signalResponse);
        if (this.unmounted) {
            return;
        }
        this.gameliftstreams?.attachInput();
        this.setState((prevState) => ({
            ...prevState,
            status: StreamState.RUNNING,
            isStreamStarting: false,
            isStatsVisible: true,
        }));
        this.startElapsedTimer();
    }

    private closeConnection() {
        this.stopElapsedTimer();
        this.setState({status: StreamState.STOPPED, inputEnabled: false, isStatsVisible: false, isProfilerArmed: false});
        this.gameliftstreams?.close();
        this.resetGameLiftStreamsSDK();
        this.setState({ isStreamStarting: false });

        if (document.fullscreenElement) {
            document.exitFullscreen().then();
        }
    }

    private enableFullScreen() {
        const element = this.getVideoElement()
        if (element) {
            this.gameliftstreams?.attachInput()
            this.setState({inputEnabled: true})
            element.requestFullscreen();
            // @ts-ignore
            if (navigator.keyboard) {
                // @ts-ignore
                const keyboard = navigator.keyboard;
                keyboard.lock(["Escape"]);
            }
        }
    }

    private streamStartedAt = 0;
    private elapsedTimer?: ReturnType<typeof setInterval>;

    private startElapsedTimer() {
        this.stopElapsedTimer();
        this.streamStartedAt = Date.now();
        this.elapsedTimer = setInterval(() => {
            this.setState({ elapsed: Math.floor((Date.now() - this.streamStartedAt) / 1000) });
        }, 1000);
    }

    private stopElapsedTimer() {
        if (this.elapsedTimer) {
            clearInterval(this.elapsedTimer);
            this.elapsedTimer = undefined;
        }
    }

    private formatElapsed(): string {
        const minutes = Math.floor(this.state.elapsed / 60).toString().padStart(2, '0');
        const seconds = (this.state.elapsed % 60).toString().padStart(2, '0');
        return `${minutes}:${seconds}`;
    }

    private regionLabel(): string {
        return REGION_LABELS[this.state.regions[0]] ?? this.state.regions[0];
    }

    private sessionShortId(): string {
        const arn = this.currentSessionArn();
        return arn ? (arn.split('/').pop() ?? arn) : '';
    }

    private toggleRejoin = () => {
        this.setState((prevState) => ({ isRejoinOpen: !prevState.isRejoinOpen }));
    };

    private copyArn = () => {
        navigator.clipboard?.writeText(this.currentSessionArn()).catch(() => {
            /* clipboard write blocked — the ARN stays visible in the bar */
        });
    };

    private pasteInProgress = false;
    private unmounted = false;

    private keyboardFilter = (event: KeyboardEvent): boolean => {
        // During replay drop new presses (no interleave) but pass releases, so a key held before
        // paste isn't left stuck down remotely. Synthetic events (isTrusted false) bypass this.
        if (this.pasteInProgress && event.isTrusted) {
            return event.type !== 'keydown';
        }
        // Exclude AltGr (Ctrl+Alt on some layouts) so AltGr+V still types its character.
        if ((event.ctrlKey || event.metaKey) && !event.altKey && !event.getModifierState('AltGraph')
            && (event.code === 'KeyV' || event.key === 'v' || event.key === 'V')) {
            if (isLocalControl(event.target)) {
                return false;
            }
            event.preventDefault();
            if (event.type === 'keydown' && !event.repeat) {
                this.pasteFromClipboard();
            }
            return false;
        }
        return true;
    };

    private pasteFromClipboard = async () => {
        if (this.unmounted || this.pasteInProgress || this.state.status !== StreamState.RUNNING || !this.gameliftstreams) {
            return;
        }
        const session = this.gameliftstreams;
        // Release held modifiers BEFORE enabling paste mode — the filter drops them once active.
        // If a release isn't transmitted the remote modifier stays down and replay would type
        // shortcuts (Ctrl+A) instead of text, so abort before typing anything.
        let releasesTransmitted = true;
        for (const [key, code] of MODIFIER_KEYS) {
            const location = code.endsWith('Right')
                ? KeyboardEvent.DOM_KEY_LOCATION_RIGHT
                : KeyboardEvent.DOM_KEY_LOCATION_LEFT;
            releasesTransmitted = session.processKeyboardEvent(new KeyboardEvent('keyup', { key, code, location })) && releasesTransmitted;
        }
        if (!releasesTransmitted) {
            alert('Paste not sent: could not release the held modifier keys on the stream. Release Ctrl/Cmd and try again.');
            return;
        }
        this.pasteInProgress = true;
        try {
            let text: string;
            try {
                text = await Promise.race([
                    navigator.clipboard.readText(),
                    new Promise<never>((_resolve, reject) =>
                        setTimeout(() => reject(new Error('clipboard read timed out')), CLIPBOARD_READ_TIMEOUT_MS)),
                ]);
            } catch (e) {
                if (!this.unmounted) {
                    alert('Could not read the clipboard — grant clipboard permission, or this browser blocks programmatic reads.');
                }
                return;
            }
            if (this.unmounted) {
                return;
            }
            if (text.length > MAX_PASTE_CHARS) {
                alert(`Paste not sent: clipboard has ${text.length} characters, over the ${MAX_PASTE_CHARS}-character limit. Shorten the value or type it manually.`);
                return;
            }
            let unsupportedCount = 0;
            for (const character of text) {
                if (!US_KEY_MAP[character]) {
                    unsupportedCount++;
                }
            }
            if (unsupportedCount > 0) {
                alert(`Paste not sent: ${unsupportedCount} character(s) can't be typed into the game (non-Latin letters, line breaks, or symbols outside a US keyboard layout). Type the value manually.`);
                return;
            }
            for (const character of text) {
                if (this.unmounted || this.gameliftstreams !== session || this.state.status !== StreamState.RUNNING) {
                    break;
                }
                const stroke = US_KEY_MAP[character];
                if (!stroke) {
                    continue;
                }
                const { code, shift } = stroke;
                const shiftLeft = KeyboardEvent.DOM_KEY_LOCATION_LEFT;
                // Track every send (no short-circuit): a dropped event = silently corrupted paste.
                let transmitted = true;
                if (shift) {
                    transmitted = session.processKeyboardEvent(new KeyboardEvent('keydown', { key: 'Shift', code: 'ShiftLeft', location: shiftLeft, shiftKey: true })) && transmitted;
                }
                transmitted = session.processKeyboardEvent(new KeyboardEvent('keydown', { key: character, code, shiftKey: shift })) && transmitted;
                transmitted = session.processKeyboardEvent(new KeyboardEvent('keyup', { key: character, code, shiftKey: shift })) && transmitted;
                if (shift) {
                    transmitted = session.processKeyboardEvent(new KeyboardEvent('keyup', { key: 'Shift', code: 'ShiftLeft', location: shiftLeft, shiftKey: false })) && transmitted;
                }
                if (!transmitted) {
                    // A keydown may have landed without its keyup; best-effort release so the key
                    // isn't left stuck down on the remote.
                    session.processKeyboardEvent(new KeyboardEvent('keyup', { key: character, code, shiftKey: false }));
                    if (shift) {
                        session.processKeyboardEvent(new KeyboardEvent('keyup', { key: 'Shift', code: 'ShiftLeft', location: shiftLeft, shiftKey: false }));
                    }
                    alert('Paste interrupted before it finished — the pasted value may be incomplete. Type it manually.');
                    break;
                }
                await new Promise(resolve => setTimeout(resolve, PASTE_KEYSTROKE_DELAY_MS));
            }
        } finally {
            this.pasteInProgress = false;
        }
    };

    private handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
        const { name, value } = event.target;
        this.setState((prevState) => ({ ...prevState, [name]: value.trim() }));
    }

    render() {
        const running = this.state.status === StreamState.RUNNING;
        const starting = this.state.isStreamStarting;
        const sessionShortId = this.sessionShortId();

        return (
            <div className="gls-app">
                <div className="gls-topbar">
                    <div className="gls-brand">GameLift Streams</div>
                    <div className="gls-vrule" />
                    {this.state.buildLabel !== '' &&
                        <div className="gls-meta">
                            <span className="gls-label">Build</span>
                            <span className="gls-value">{this.state.buildLabel}</span>
                        </div>
                    }
                    {running && sessionShortId !== ''
                        ? <>
                            <div className="gls-meta">
                                <span className="gls-label">Session</span>
                                <span className="gls-value">{sessionShortId}</span>
                            </div>
                            <button className="gls-btn gls-btn--ghost gls-btn--sm" onClick={this.copyArn}>Copy ARN</button>
                        </>
                        : <>
                            {this.state.sgId !== '' &&
                                <div className="gls-meta">
                                    <span className="gls-label">SG</span>
                                    <span className="gls-value gls-value--muted">{this.state.sgId}</span>
                                </div>
                            }
                            {this.state.appId !== '' &&
                                <div className="gls-meta">
                                    <span className="gls-label">App</span>
                                    <span className="gls-value gls-value--muted">{this.state.appId}</span>
                                </div>
                            }
                        </>
                    }
                    <div className="gls-spacer" />
                    {this.props.user?.nick && <span className="gls-user">{this.props.user.nick}</span>}
                    <button className="gls-btn gls-btn--ghost gls-btn--sm" onClick={this.props.signOut}>Sign out</button>
                </div>

                <div className={starting ? 'gls-actionbar is-starting' : 'gls-actionbar'}>
                    {!running
                        ? <button className="gls-btn gls-btn--primary" onClick={this.createStreamSession} disabled={starting}>
                            {starting ? 'Starting…' : 'Start stream'}
                        </button>
                        : <button className="gls-btn gls-btn--ink" onClick={this.closeConnection}>End stream</button>
                    }
                    {running &&
                        <button className="gls-btn gls-btn--ghost" onClick={this.enableFullScreen}>Fullscreen</button>
                    }
                    {running &&
                        <button
                            className={this.state.isStatsVisible ? 'gls-btn gls-btn--ink' : 'gls-btn gls-btn--ghost'}
                            onClick={this.toggleStats}
                        >{this.state.isStatsVisible ? 'Stats on' : 'Stats off'}</button>
                    }
                    {running &&
                        <button
                            className={this.state.isProfilerArmed ? 'gls-btn gls-btn--ink' : 'gls-btn gls-btn--ghost'}
                            onClick={this.toggleProfiler}
                            title="Alt+F11 — arm the in-game profiler"
                        >{this.state.isProfilerArmed ? 'Profiler on' : 'Profiler off'}</button>
                    }
                    {running && this.state.isProfilerArmed &&
                        <button
                            className="gls-btn gls-btn--ghost"
                            onClick={this.dumpProfiler}
                            title="F11 — write a capture to .game_logs/ring-*.dap"
                        >Capture ⤓</button>
                    }
                    {this.currentSessionArn() !== '' &&
                        <button
                            className={running ? 'gls-btn gls-btn--primary gls-btn--report' : 'gls-btn gls-btn--ghost'}
                            onClick={this.reportBug}
                        >Report a bug ↗</button>
                    }
                    {!running && <>
                        <div className="gls-vrule gls-vrule--short" />
                        <div className="gls-meta">
                            <span className="gls-label">Region</span>
                            <span className="gls-value">{this.regionLabel()}</span>
                        </div>
                        <button
                            className={this.state.isRejoinOpen ? 'gls-btn gls-btn--ink gls-btn--sm' : 'gls-btn gls-btn--ghost gls-btn--sm'}
                            onClick={this.toggleRejoin}
                        >Rejoin a session</button>
                    </>}
                    <div className="gls-spacer" />
                    {running
                        ? <div className="gls-live">
                            <span className="gls-live__dot" />
                            <span className="gls-live__time">LIVE · {this.formatElapsed()}</span>
                        </div>
                        : <div className="gls-meta">
                            <span className="gls-label">Status</span>
                            <span className="gls-status">{starting ? 'Provisioning' : 'Idle'}</span>
                        </div>
                    }
                </div>

                {this.state.isRejoinOpen && !running &&
                    <div className="gls-rejoin">
                        <label className="gls-field">
                            <span className="gls-label">Stream session ARN</span>
                            <input
                                className="gls-input"
                                type="text"
                                name="sessionId"
                                value={this.state.sessionId}
                                placeholder="arn:aws:gameliftstreams:eu-central-1:…:streamsession/<streamGroupId>/<sessionId>"
                                onChange={this.handleInputChange}
                            />
                        </label>
                        <button className="gls-btn gls-btn--ink" onClick={this.createStreamSessionConnection} disabled={starting}>Reconnect</button>
                    </div>
                }

                <div className="gls-stage">
                    <video id={'StreamVideoElement'} autoPlay playsInline className="gls-video" />
                    {!running &&
                        <div className="gls-stage__idle">
                            <div className="gls-label gls-label--accent">Signal</div>
                            <div className="gls-stage__line">{starting ? 'Provisioning a session…' : 'No stream attached'}</div>
                            <div className="gls-stage__sub">
                                {starting
                                    ? `polling session status every 1 s · timeout ${SESSION_WAIT_TIMEOUT_MS / 1000} s`
                                    : 'Press Start stream · provisioning takes seconds to minutes'}
                            </div>
                        </div>
                    }
                    <StatsOverlay
                        gameliftstreams={this.gameliftstreams}
                        perfStats={this.state.perfStats}
                        isVisible={this.state.isStatsVisible}
                        isStreamRunning={this.state.status === StreamState.RUNNING}
                    />
                </div>
                <audio id={'StreamAudioElement'} autoPlay></audio>
            </div>
        );
    }
}

export default StreamComponent;
